/**
 * «Я гость отеля» — само-авторизация гостя (Phase 3 гостевого входа).
 *
 * ОТДЕЛЬНЫЙ ЭКРАН (роут /guest-login), доступен со страницы входа по ссылке
 * «Я гость отеля». Анонимный: tenant резолвится по поддомену (X-Tenant-Slug),
 * JWT не требуется.
 *
 * Поток:
 *   Форма: фамилия (регистр не важен — бэк матчит без учёта регистра) + номер
 *   + даты заезд/выезд → POST /v1/guest-auth/self.
 *     · 200 authorized → сохраняем токены, входим как гость (redirect «/»).
 *     · 202 pending_approval → экран «Заявка отправлена, ожидайте» + подсказка
 *       привязать Telegram (deep-link/QR на бота отеля).
 * Кнопки — снизу, «Назад» → на страницу входа.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@hsc/auth';
import { guestSelfAuth, getPublicRooms } from '@hsc/api-client';
import { useTenant } from '@hsc/tenant';
import { writeLocalAccessToken, writeLocalRefreshToken } from '../lib/storage.js';
import { PublicTrustFooter } from '../shared/ui/PublicTrustFooter.js';
import { QrCanvas } from '../features/guest-access/QrCanvas.js';

function dateOffset(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Deep-link бота отеля по slug тенанта (генерик): t.me/<slug>Hotel_bot — это
 * соглашение бренда (например, oblaka → OblakaHotel_bot). Если slug нет —
 * вернём пусто, QR/ссылку не показываем.
 */
function botDeeplink(slug: string | null): string | null {
  if (!slug) return null;
  const cap = slug.charAt(0).toUpperCase() + slug.slice(1);
  return `https://t.me/${cap}Hotel_bot`;
}

export function GuestSelfAuthRoute(): JSX.Element {
  const navigate = useNavigate();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { slug } = useTenant();

  const [surname, setSurname] = useState('');
  const [roomNumber, setRoomNumber] = useState('');
  const [checkIn, setCheckIn] = useState(dateOffset(0));
  const [checkOut, setCheckOut] = useState(dateOffset(1));
  const [pending, setPending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Номера отеля для выпадайки (анон GET /v1/public/{slug}/rooms). Пока грузим —
  // select disabled («Загрузка…»); при ошибке падаем в текст-инпут, чтобы гость
  // всё равно мог войти.
  const [rooms, setRooms] = useState<string[]>([]);
  const [roomsState, setRoomsState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    if (!slug) {
      setRoomsState('error');
      return;
    }
    let alive = true;
    setRoomsState('loading');
    getPublicRooms(slug)
      .then((res) => {
        if (!alive) return;
        setRooms(res.rooms.map((r) => r.number));
        setRoomsState('ready');
      })
      .catch(() => {
        if (!alive) return;
        setRoomsState('error');
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  const datesValid = checkIn !== '' && checkOut !== '' && checkOut > checkIn;
  const canSubmit =
    surname.trim() !== '' && roomNumber.trim() !== '' && datesValid && !busy;

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setBusy(true);
    try {
      const res = await guestSelfAuth({
        surname: surname.trim(),
        room_number: roomNumber.trim(),
        check_in: checkIn,
        check_out: checkOut,
      });
      if (res.outcome === 'pending') {
        // Кэшируем выданные на pending токены — после подтверждения персоналом
        // обновление страницы авто-логинит гостя (refresh оживёт, когда грант
        // станет active). Не делаем auth.login сейчас: остаёмся на экране
        // ожидания, вход произойдёт при следующем заходе/обновлении.
        if (res.access_token) writeLocalAccessToken(res.access_token);
        if (res.refresh_token) writeLocalRefreshToken(res.refresh_token);
        setPending(true);
        return;
      }
      // authorized → сохраняем токены и входим как гость.
      writeLocalAccessToken(res.access_token);
      if (res.refresh_token) writeLocalRefreshToken(res.refresh_token);
      queryClient.clear();
      auth.login(res.access_token);
      navigate('/', { replace: true });
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 400) {
        setError('Проверьте номер комнаты и даты — что-то не совпадает.');
      } else if (typeof status === 'number' && status >= 500) {
        setError('Сервис временно недоступен. Попробуйте чуть позже.');
      } else {
        setError('Не удалось войти. Проверьте данные и попробуйте снова.');
      }
    } finally {
      setBusy(false);
    }
  };

  /* ---------- Экран «Заявка отправлена» (202 pending) ---------- */
  if (pending) {
    const link = botDeeplink(slug);
    return (
      <main className="hsc-login">
        <div className="hsc-login__box" data-testid="guest-pending-card">
          <div className="hsc-login__brand">
            <span aria-hidden="true" className="hsc-login__logo">L</span>
            <h1 className="hsc-login__title">Заявка отправлена</h1>
            <p className="hsc-login__subtitle">Ожидайте подтверждения</p>
          </div>
          <p className="ga-result-lead">
            Мы не нашли активного заселения по этим данным, поэтому передали заявку администратору
            отеля. Как только её подтвердят, Вы сможете войти. Это обычно занимает несколько минут.
          </p>

          {link && (
            <div className="ga-qr-wrap" data-testid="guest-pending-tg">
              <p className="ga-qr-cap">Привяжите Telegram — уведомим о подтверждении быстрее:</p>
              <QrCanvas value={link} size={180} testId="guest-pending-qr" />
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="anon__login-link"
                data-testid="guest-pending-tg-link"
              >
                Открыть бота отеля в Telegram
              </a>
            </div>
          )}

          <div className="staff-modal__actions ga-actions" style={{ marginTop: '1rem' }}>
            <button
              type="button"
              className="staff-btn staff-btn--ghost"
              onClick={() => navigate('/login')}
              data-testid="guest-pending-back"
            >
              На страницу входа
            </button>
            <button
              type="button"
              className="staff-btn"
              data-testid="guest-pending-retry"
              onClick={() => {
                setPending(false);
                setError(null);
              }}
            >
              Ввести данные заново
            </button>
          </div>
          <PublicTrustFooter />
        </div>
      </main>
    );
  }

  /* ---------- Экран ФОРМЫ ---------- */
  return (
    <main className="hsc-login">
      <div className="hsc-login__box" data-testid="guest-self-auth-card">
        <div className="hsc-login__brand">
          <span aria-hidden="true" className="hsc-login__logo">L</span>
          <h1 className="hsc-login__title">Вход для гостя</h1>
          <p className="hsc-login__subtitle">Укажите данные Вашего заселения</p>
        </div>

        <form onSubmit={onSubmit} noValidate data-testid="guest-self-auth-form">
          <div className="ta-field">
            <label className="hsc-input__label" htmlFor="gs-surname">
              Фамилия
            </label>
            <input
              id="gs-surname"
              className="ta-input"
              value={surname}
              onChange={(e) => setSurname(e.target.value)}
              placeholder="как в брони"
              autoComplete="off"
              data-testid="guest-surname"
              required
            />
          </div>

          <div className="ta-field">
            <label className="hsc-input__label" htmlFor="gs-room">
              Номер комнаты
            </label>
            {roomsState === 'error' ? (
              // Фолбэк: список номеров не загрузился — даём текст-инпут, чтобы
              // гость всё равно мог войти.
              <input
                id="gs-room"
                className="ta-input"
                value={roomNumber}
                onChange={(e) => setRoomNumber(e.target.value)}
                placeholder="например, 305"
                inputMode="numeric"
                data-testid="guest-room"
                required
              />
            ) : (
              <select
                id="gs-room"
                className="ta-select"
                value={roomNumber}
                onChange={(e) => setRoomNumber(e.target.value)}
                disabled={roomsState === 'loading'}
                data-testid="guest-room"
                required
              >
                <option value="" disabled>
                  {roomsState === 'loading' ? 'Загрузка…' : 'Выберите номер'}
                </option>
                {rooms.map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="ta-field">
            <span className="hsc-input__label">Даты проживания</span>
            <div className="ga-dates">
              <label className="ga-dates__col">
                <span className="ga-dates__cap">Заезд</span>
                <input
                  type="date"
                  className="ta-input"
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                  data-testid="guest-checkin"
                  required
                />
              </label>
              <label className="ga-dates__col">
                <span className="ga-dates__cap">Выезд</span>
                <input
                  type="date"
                  className="ta-input"
                  value={checkOut}
                  min={checkIn}
                  onChange={(e) => setCheckOut(e.target.value)}
                  data-testid="guest-checkout"
                  required
                />
              </label>
            </div>
          </div>

          {error && (
            <div role="alert" className="hsc-login__error" data-testid="guest-self-auth-error">
              {error}
            </div>
          )}

          <div className="staff-modal__actions ga-actions" style={{ marginTop: '1rem' }}>
            <button
              type="button"
              className="staff-btn staff-btn--ghost"
              onClick={() => navigate('/login')}
              data-testid="guest-self-auth-back"
            >
              Назад
            </button>
            <button
              type="submit"
              className="staff-btn"
              disabled={!canSubmit}
              data-testid="guest-self-auth-submit"
            >
              {busy ? 'Проверяем…' : 'Войти'}
            </button>
          </div>
        </form>

        <p className="hsc-login__anon-hint" style={{ marginTop: '0.75rem' }}>
          Фамилию можно вводить в любом регистре. Если заселение ещё не оформлено — заявка уйдёт
          администратору на подтверждение.
        </p>
        <PublicTrustFooter />
      </div>
    </main>
  );
}
